/**
 * User Detail Page
 * Display detailed user information and activity
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableHead,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  Tooltip,
  IconButton,
  Skeleton,
  TablePagination,
  InputBase,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMessage } from '@/contexts/MessageContext';
import {
  CheckCircle as ApproveIcon,
  Warning as WarningIcon,
  Block as SuspendIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { UserService } from '../../../services/user/UserService';
import { GhostVerificationService } from '../../../services/ghost/GhostVerificationService';
import type { UserDetail, UserWarning } from '../../../types/user';
import { MissionsTab } from './components/tabs/MissionsTab';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  PrimaryButton,
} from '../../../components/ui';
import { formatDateTime } from '@/utils/dateUtils';
import { IssueWarningDialog } from './components/IssueWarningDialog';

type TabValue = 'tasks' | 'payments' | 'warnings';

const getUserStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'disabled':
      return 'Disabled';
    case 'pending':
      return 'Pending';
    case 'suspended':
      return 'Suspended';
    default:
      return status;
  }
};

const getStatusColor = (status: string, theme: any): string => {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'disabled':
      return theme.palette.error.main;
    case 'pending':
      return theme.palette.warning.main;
    case 'suspended':
      return theme.palette.grey[500];
    default:
      return theme.palette.text.secondary;
  }
};

const UserDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showError } = useMessage();
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('tasks');

  // Suspension dialog state
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendUntil, setSuspendUntil] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [isSuspending, setIsSuspending] = useState(false);

  // Activity filtering state
  const [activitySearchQuery, setActivitySearchQuery] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('All');

  // Warnings pagination state
  const [warnings, setWarnings] = useState<UserWarning[]>([]);
  const [totalWarnings, setTotalWarnings] = useState(0);
  const [warningPage, setWarningPage] = useState(1);
  const [warningRowsPerPage, setWarningRowsPerPage] = useState(5);
  const [warningKeyword, setWarningKeyword] = useState('');
  const [isWarningsLoading, setIsWarningsLoading] = useState(false);

  // Activation dialog state
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateReason, setActivateReason] = useState('');
  const [isActivating, setIsActivating] = useState(false);

  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const fetchUserDetail = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const detail = await UserService.getUserDetail(id);
      // If this is an Asset user, redirect to Asset detail page
      if (detail && detail.userType === 'asset') {
        navigate(`/users/asset/${id}`, { replace: true });
        return;
      }
      setUserDetail(detail);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
      setUserDetail(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWarnings = async () => {
    if (!id) return;
    try {
      setIsWarningsLoading(true);
      const res = await UserService.getWarningHistory(id, warningKeyword, warningPage, warningRowsPerPage);
      setWarnings(res.data);
      setTotalWarnings(res.total);
    } catch (error) {
      console.error('Failed to fetch user warnings:', error);
      showError('Failed to load warning history.');
    } finally {
      setIsWarningsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetail();
  }, [id, navigate, showError]);

  useEffect(() => {
    if (id) {
      fetchWarnings();
    }
  }, [id, warningPage, warningRowsPerPage, warningKeyword]);

  // If user is not Ghost and activeTab is 'tasks', switch to 'payments'
  useEffect(() => {
    if (userDetail && userDetail.userType !== 'ghost' && activeTab === 'tasks') {
      setActiveTab('payments');
    }
  }, [userDetail, activeTab]);

  const handleIssueWarning = () => {
    setWarningDialogOpen(true);
  };

  const handleIssueWarningConfirm = async (reason: string) => {
    if (!id) return;

    try {
      await UserService.issueWarning(id, reason);

      // Refresh user detail to show the new warning if it's a real API
      const detail = await UserService.getUserDetail(id);
      if (detail) {
        setUserDetail(detail);
      }

      // Refresh warnings
      fetchWarnings();

      setWarningDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to issue warning:', error);
      showError(error.message || 'Failed to issue warning.');
      throw error; // Let the dialog know it failed
    }
  };

  const handleSuspendUser = () => {
    setSuspendDialogOpen(true);
  };

  const handleSuspendDialogClose = () => {
    setSuspendDialogOpen(false);
    setSuspendUntil('');
    setSuspendReason('');
  };

  const handleSuspendConfirm = async () => {
    if (!id || !suspendUntil || !suspendReason) return;

    try {
      setIsSuspending(true);
      const suspendUntilISO = new Date(suspendUntil).toISOString();
      await UserService.suspendUser(id, suspendUntilISO, suspendReason);

      // Refresh user detail
      const detail = await UserService.getUserDetail(id);
      if (detail) {
        setUserDetail(detail);
      }

      handleSuspendDialogClose();
    } catch (error: any) {
      console.error('Failed to suspend user:', error);
      showError(error.message || 'Failed to suspend user.');
    } finally {
      setIsSuspending(false);
    }
  };

  const handleActivateUser = () => {
    setActivateDialogOpen(true);
  };

  const handleActivateDialogClose = () => {
    setActivateDialogOpen(false);
    setActivateReason('');
  };

  const handleActivateConfirm = async () => {
    if (!id || !activateReason) return;

    try {
      setIsActivating(true);
      await UserService.activateUser(id, activateReason);

      // Refresh user detail
      const detail = await UserService.getUserDetail(id);
      if (detail) {
        setUserDetail(detail);
      }

      handleActivateDialogClose();
    } catch (error: any) {
      console.error('Failed to activate user:', error);
      showError(error.message || 'Failed to activate user.');
    } finally {
      setIsActivating(false);
    }
  };

  const handleMissionCodeClick = (missionId?: string) => {
    if (!missionId) return;
    const selectedText = window.getSelection()?.toString().trim();
    if (selectedText) return;
    navigate(`/missions/${missionId}`);
  };

  const getAvatarInitials = (name: string): string => {
    if (!name) return 'A';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!userDetail) {
    return (
      <MainLayout>
        <EmptyState
          title="User Not Found"
          message="The user you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  return (
    <>
      <MainLayout>
        {/* Page Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: theme.spacing(3),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {userDetail.userType === 'ghost' ? 'Ghost' : 'User'}: {userDetail.name}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                padding: theme.spacing(0.5, 1.5),
                borderRadius: theme.shapes.borderRadius.lg,
                backgroundColor: getStatusColor(userDetail.status, theme),
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              {getUserStatusLabel(userDetail.status)}
            </Box>
          </Box>

          {userDetail.userType === 'ghost' && (
            <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
              <PrimaryButton
                startIcon={<WarningIcon />}
                onClick={handleIssueWarning}
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.common.black,
                  padding: theme.spacing(1, 2),
                  '&:hover': {
                    backgroundColor: theme.palette.warning.dark,
                  },
                }}
              >
                Issue Warning
              </PrimaryButton>
              {userDetail.status === 'suspended' ? (
                <PrimaryButton
                  startIcon={<ApproveIcon />}
                  onClick={handleActivateUser}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    padding: theme.spacing(1, 2),
                    '&:hover': {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  Activate User
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  startIcon={<SuspendIcon />}
                  onClick={handleSuspendUser}
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    padding: theme.spacing(1, 2),
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }}
                >
                  Suspend User
                </PrimaryButton>
              )}
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - User Profile */}
          <Grid item xs={12} md={4}>
            <Card sx={{ marginBottom: theme.spacing(3) }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: theme.spacing(3) }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      marginBottom: theme.spacing(2),
                      backgroundColor: theme.palette.primary.main,
                      fontSize: '3rem',
                      fontWeight: 600,
                      boxShadow: `0 4px 12px rgba(74, 144, 226, 0.3)`,
                    }}
                    src={userDetail.avatar}
                  >
                    {!userDetail.avatar && getAvatarInitials(userDetail.name)}
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(0.75),
                      fontSize: '1.5rem',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {userDetail.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: theme.spacing(2),
                      fontSize: '0.875rem',
                    }}
                  >
                    {userDetail.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip
                      label={userDetail.userType === 'ghost' ? 'Ghost' : 'Asset'}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.common.white,
                        fontWeight: theme.typography.subtitle2.fontWeight,
                        fontSize: theme.typography.caption.fontSize,
                        height: 26,
                        borderRadius: theme.shapes.borderRadius.md,
                        padding: theme.spacing(0, 1.25),
                      }}
                    />
                    {userDetail.userType === 'ghost' && userDetail.tier && (
                      <Chip
                        label={`Tier ${userDetail.tier}`}
                        size="small"
                        sx={{
                          backgroundColor: theme.customColors.searchBackground,
                          color: theme.palette.text.primary,
                          fontWeight: theme.typography.subtitle2.fontWeight,
                          fontSize: theme.typography.caption.fontSize,
                          height: 26,
                          borderRadius: theme.shapes.borderRadius.md,
                          padding: theme.spacing(0, 1.25),
                          border: `1px solid ${theme.customColors.tableBorder}`,
                        }}
                      />
                    )}
                    <Chip
                      label={getUserStatusLabel(userDetail.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(userDetail.status, theme),
                        color: '#FFFFFF',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 26,
                        borderRadius: theme.shapes.borderRadius.md,
                        padding: theme.spacing(0, 1.25),
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ marginBottom: theme.spacing(1.5) }} />

                {/* Earnings Overview - Highlighted */}
                {userDetail.earnings && userDetail.userType === 'ghost' && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: theme.customColors.searchBackground,
                      borderRadius: `${theme.shapes.borderRadius.lg}px`,
                      padding: theme.spacing(2),
                      marginBottom: theme.spacing(2),
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: `1px solid ${theme.customColors.tableBorder}`,
                    }}
                  >
                    {/* Left Section - Total Earnings */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          marginBottom: theme.spacing(0.5),
                          display: 'block',
                          fontWeight: 500,
                        }}
                      >
                        Total Earnings
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          fontSize: '1.75rem',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        ${(userDetail.earnings.totalEarnings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>

                    {/* Right Section - Total Payout */}
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          display: 'block',
                          marginBottom: theme.spacing(0.5),
                          fontWeight: 500,
                        }}
                      >
                        Total Payout
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          fontSize: '1rem',
                          lineHeight: 1.2,
                        }}
                      >
                        ${(userDetail.earnings.totalPayout || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Paper>
                )}

                {/* User Information - Two Column Layout */}
                <Grid container spacing={2} sx={{ marginBottom: theme.spacing(2) }}>
                  {userDetail.userType === 'ghost' && userDetail.tier && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          Account Tier:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {userDetail.tier}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {userDetail.license?.yearsOfExperience !== undefined && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          Years of Experience:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {userDetail.license.yearsOfExperience} Years
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {userDetail.license?.licenseNumber && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          License Number:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {userDetail.license.licenseNumber}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {userDetail.personalInfo?.phone && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          Phone:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {userDetail.personalInfo.phone}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Bond Information - integrated into left column for Ghost */}
                  {userDetail.userType === 'ghost' && (
                    <>
                      {/* Bond Amount */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.5), display: 'block' }}>
                          Bond Amount:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.875rem' }}>
                          {userDetail.bondInfo?.bondAmount !== undefined ? `$${userDetail.bondInfo.bondAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                        </Typography>
                      </Grid>

                      {/* Bond Number */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.5), display: 'block' }}>
                          Bond Number:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.875rem' }}>
                          {userDetail.bondInfo?.bondNumber}
                        </Typography>
                      </Grid>

                      {/* Bond Company Name */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.5), display: 'block' }}>
                          Bond Company:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.875rem' }}>
                          {userDetail.bondInfo?.bondCompanyName}
                        </Typography>
                      </Grid>

                      {/* NAIC Number */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.5), display: 'block' }}>
                          NAIC Number:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.875rem' }}>
                          {userDetail.bondInfo?.naicNumber}
                        </Typography>
                      </Grid>

                      {/* Bond Expiry Date */}
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.5), display: 'block' }}>
                          Bond Expiry Date:
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: userDetail.bondInfo?.bondExpiryDate && new Date(userDetail.bondInfo.bondExpiryDate) < new Date() ? theme.palette.error.main : theme.palette.text.primary,
                          fontWeight: 500,
                          fontSize: '0.875rem'
                        }}>
                          {userDetail.bondInfo?.bondExpiryDateText}
                          {userDetail.bondInfo?.bondExpiryDate && new Date(userDetail.bondInfo.bondExpiryDate) < new Date() && (
                            <Chip label="Expired" size="small" sx={{ marginLeft: theme.spacing(1), backgroundColor: theme.palette.error.main, color: theme.palette.error.contrastText, fontSize: '0.625rem', height: 18 }} />
                          )}
                        </Typography>
                      </Grid>

                      {/* Bond Document */}
                      {userDetail.bondInfo?.bondDocument && (
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', marginBottom: theme.spacing(0.75), display: 'block' }}>
                            Bond Document:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), padding: theme.spacing(1), backgroundColor: theme.customColors.searchBackground, border: `1px solid ${theme.customColors.tableBorder}`, borderRadius: 1 }}>
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                              <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.primary, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {userDetail.bondInfo.bondDocument.fileName}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                rel="noopener noreferrer"
                                target="_blank"
                                component="a"
                                href={userDetail.bondInfo.bondDocument.url}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.primary.lighter,
                                  }
                                }}
                              >
                                <ViewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                rel="noopener noreferrer"
                                target="_blank"
                                component="a"
                                href={userDetail.bondInfo.bondDocument.url}
                                download={userDetail.bondInfo.bondDocument.fileName}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.primary.lighter,
                                  }
                                }}
                              >
                                <DownloadIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}

                  {userDetail.personalInfo?.dateOfBirth && (
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          Date of Birth:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {userDetail.personalInfo.dateOfBirth}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {/* Long content - Full width */}
                  {userDetail.personalInfo?.address && (
                    <Grid item xs={12}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.5),
                            display: 'block',
                          }}
                        >
                          Address:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {userDetail.personalInfo.address}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {userDetail.license?.licensedStates && userDetail.license.licensedStates.length > 0 && (
                    <Grid item xs={12}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            marginBottom: theme.spacing(0.75),
                            display: 'block',
                          }}
                        >
                          Licensed States:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: theme.spacing(0.75), flexWrap: 'wrap' }}>
                          {userDetail.license.licensedStates.map((state) => (
                            <Chip
                              key={state}
                              label={state}
                              size="small"
                              sx={{
                                backgroundColor: theme.customColors.searchBackground,
                                color: theme.palette.text.primary,
                                fontSize: '0.75rem',
                                height: 24,
                                borderRadius: theme.shapes.borderRadius.sm,
                                '& .MuiChip-label': {
                                  padding: theme.spacing(0, 1),
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          marginBottom: theme.spacing(0.5),
                          display: 'block',
                        }}
                      >
                        Last Login:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        {userDetail.lastLoginText || 'Never'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          marginBottom: theme.spacing(0.5),
                          display: 'block',
                        }}
                      >
                        Join Date:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        {formatDateTime(userDetail.createdAt)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Tabs and Content */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent noPadding>
                {/* Tabs */}
                <Box sx={{ borderBottom: `1px solid ${theme.customColors.tableBorder}` }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        '&.Mui-selected': {
                          color: theme.palette.primary.main,
                        },
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {userDetail.userType === 'ghost' && (
                      <Tab label="Assigned Missions" value="tasks" />
                    )}
                    <Tab label={userDetail.userType === 'ghost' ? 'Payout History' : 'Payment History'} value="payments" />
                    <Tab label="Warning History" value="warnings" />
                  </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ padding: theme.spacing(3) }}>
                  {activeTab === 'tasks' && userDetail.userType === 'ghost' && (
                    <MissionsTab userDetail={userDetail} />
                  )}

                  {activeTab === 'payments' && (
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          marginBottom: theme.spacing(2),
                          color: theme.palette.text.primary,
                          fontSize: '1rem',
                        }}
                      >
                        {userDetail.userType === 'ghost' ? 'Payout History' : 'Payment History'}
                      </Typography>
                      {!userDetail.payments || userDetail.payments.length === 0 ? (
                        <EmptyState
                          title={userDetail.userType === 'ghost' ? 'No Payouts Found' : 'No Payments Found'}
                          message={userDetail.userType === 'ghost' ? 'There are no payout records for this user.' : 'There are no payment records for this user.'}
                        />
                      ) : (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeaderCell>Date</TableHeaderCell>
                              {userDetail.userType === 'ghost' && <TableHeaderCell>Mission Code</TableHeaderCell>}
                              {userDetail.userType === 'ghost' && <TableHeaderCell>Mission Title</TableHeaderCell>}
                              <TableHeaderCell>Amount</TableHeaderCell>
                              <TableHeaderCell>Status</TableHeaderCell>
                              {userDetail.userType === 'ghost' && <TableHeaderCell>Payout Account</TableHeaderCell>}
                              {userDetail.userType !== 'ghost' && <TableHeaderCell>Description</TableHeaderCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {userDetail.payments.map((payment) => (
                              <TableRow key={payment.id}>
                                <TableCell>
                                  {payment.dateText}
                                </TableCell>
                                {userDetail.userType === 'ghost' && (
                                  <TableCell>
                                    {payment.missionId && payment.missionCode && payment.missionCode !== '-' ? (
                                      <Box
                                        component="span"
                                        onClick={() => handleMissionCodeClick(payment.missionId)}
                                        sx={{
                                          color: theme.palette.primary.main,
                                          fontSize: '0.875rem',
                                          fontWeight: 500,
                                          userSelect: 'text',
                                          cursor: 'pointer',
                                          '&:hover': {
                                            textDecoration: 'underline',
                                          },
                                        }}
                                      >
                                        {payment.missionCode}
                                      </Box>
                                    ) : (
                                      <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                                        -
                                      </Box>
                                    )}
                                  </TableCell>
                                )}
                                {userDetail.userType === 'ghost' && (
                                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {payment.missionTitle || '-'}
                                  </TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 500 }}>
                                  ${payment.amount.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={payment.status}
                                    size="small"
                                    sx={{
                                      backgroundColor: payment.status === 'completed' ? theme.palette.success.main : theme.palette.warning.main,
                                      color: theme.palette.common.white,
                                      fontWeight: theme.typography.subtitle2.fontWeight,
                                    }}
                                  />
                                </TableCell>
                                {userDetail.userType === 'ghost' && (
                                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {payment.payoutAccount || '-'}
                                  </TableCell>
                                )}
                                {userDetail.userType !== 'ghost' && (
                                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                                    {payment.description || '-'}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  )}

                  {activeTab === 'warnings' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                          }}
                        >
                          Warning History
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative',
                            borderRadius: theme.shapes.borderRadius.md,
                            backgroundColor: theme.customColors.searchBackground,
                            border: `1px solid ${theme.customColors.searchBorder}`,
                            width: 250,
                          }}
                        >
                          <Box
                            sx={{
                              padding: theme.spacing(0, 1),
                              height: '100%',
                              position: 'absolute',
                              pointerEvents: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                          </Box>
                          <InputBase
                            placeholder="Search warnings..."
                            value={warningKeyword}
                            onChange={(e) => {
                              setWarningKeyword(e.target.value);
                              setWarningPage(1);
                            }}
                            sx={{
                              color: theme.palette.text.primary,
                              width: '100%',
                              padding: theme.spacing(1, 1, 1, 4),
                              fontSize: '0.875rem',
                            }}
                          />
                        </Box>
                      </Box>

                      {isWarningsLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                        </Box>
                      ) : !warnings || warnings.length === 0 ? (
                        <EmptyState
                          title="No Warnings"
                          message="This user has no warning history."
                        />
                      ) : (
                        <>
                          <Table>
                            <TableHead>
                              <MuiTableRow>
                                <MuiTableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Reason / Message
                                </MuiTableCell>
                                <MuiTableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Issued By
                                </MuiTableCell>
                                <MuiTableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                  Date
                                </MuiTableCell>
                              </MuiTableRow>
                            </TableHead>
                            <TableBody>
                              {warnings.map((warning) => (
                                <MuiTableRow key={warning.id}>
                                  <MuiTableCell sx={{ color: theme.palette.text.primary }}>{warning.reason}</MuiTableCell>
                                  <MuiTableCell sx={{ color: theme.palette.text.secondary }}>{warning.issuedByName}</MuiTableCell>
                                  <MuiTableCell sx={{ color: theme.palette.text.secondary }}>{formatDateTime(warning.date)}</MuiTableCell>
                                </MuiTableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {totalWarnings > 0 && (
                            <TablePagination
                              component="div"
                              count={totalWarnings}
                              page={warningPage - 1}
                              onPageChange={(_, newPage) => setWarningPage(newPage + 1)}
                              rowsPerPage={warningRowsPerPage}
                              onRowsPerPageChange={(e) => {
                                setWarningRowsPerPage(parseInt(e.target.value, 10));
                                setWarningPage(1);
                              }}
                              rowsPerPageOptions={[5, 10, 25]}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}

                </Box>
              </CardContent>
            </Card>

            {/* Right Column Cards - Only for Ghost users */}
            {userDetail.userType === 'ghost' && (
              <Grid container spacing={3} sx={{ marginTop: theme.spacing(0) }}>
                {/* Performance Metrics Card */}
                {userDetail.performanceMetrics && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                          }}
                        >
                          Performance Metrics
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(0.5) }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: '0.875rem',
                                }}
                              >
                                Completion Rate
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {userDetail.performanceMetrics.completionRate || 0}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={userDetail.performanceMetrics.completionRate || 0}
                              sx={{
                                height: 8,
                                borderRadius: theme.shapes.borderRadius.md,
                                backgroundColor: theme.customColors.searchBackground,
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: theme.palette.success.main,
                                },
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.875rem',
                                marginBottom: theme.spacing(0.5),
                              }}
                            >
                              Average Rating
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                              <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {userDetail.performanceMetrics.averageRating || 0}
                              </Typography>
                            </Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.875rem',
                                marginBottom: theme.spacing(0.5),
                              }}
                            >
                              Missions Completed
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontSize: '0.875rem',
                              }}
                            >
                              {userDetail.performanceMetrics.casesCompleted || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Professional Skills Card */}
                {userDetail.skills && userDetail.skills.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                          }}
                        >
                          Professional Skills
                        </Typography>
                        <Box sx={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                          {userDetail.skills.map((skill) => (
                            <Chip
                              key={skill}
                              label={skill}
                              size="small"
                              sx={{
                                backgroundColor: theme.customColors.searchBackground,
                                color: theme.palette.text.primary,
                                borderRadius: theme.shapes.borderRadius.md,
                                fontSize: '0.875rem',
                                height: 28,
                                fontWeight: 500,
                                '& .MuiChip-label': {
                                  padding: theme.spacing(0, 1.5),
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Suspension Dialog */}
        <Dialog open={suspendDialogOpen} onClose={handleSuspendDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Suspend User</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide the suspension details for {userDetail?.name}.
              </Typography>

              <TextField
                fullWidth
                label="Suspension Date & Time"
                type="datetime-local"
                value={suspendUntil}
                onChange={(e) => setSuspendUntil(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Reason for Suspension"
                multiline
                rows={3}
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Please provide a reason for the suspension..."
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSuspendDialogClose} disabled={isSuspending}>
              Cancel
            </Button>
            <Button
              onClick={handleSuspendConfirm}
              variant="contained"
              color="error"
              disabled={!suspendUntil || !suspendReason || isSuspending}
            >
              {isSuspending ? 'Suspending...' : 'Suspend User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activation Dialog */}
        <Dialog open={activateDialogOpen} onClose={handleActivateDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Activate User</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide a reason for activating {userDetail?.name}.
              </Typography>

              <TextField
                fullWidth
                label="Reason for Activation"
                multiline
                rows={3}
                value={activateReason}
                onChange={(e) => setActivateReason(e.target.value)}
                placeholder="Please provide a reason for the activation..."
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleActivateDialogClose} disabled={isActivating}>
              Cancel
            </Button>
            <Button
              onClick={handleActivateConfirm}
              variant="contained"
              color="success"
              disabled={!activateReason || isActivating}
            >
              {isActivating ? 'Activating...' : 'Activate User'}
            </Button>
          </DialogActions>
        </Dialog>

        <IssueWarningDialog
          open={warningDialogOpen}
          onClose={() => setWarningDialogOpen(false)}
          onConfirm={handleIssueWarningConfirm}
          userName={userDetail.name}
        />
      </MainLayout>
    </>
  );
};

export default UserDetailPage;
